import type { ComponentChildren } from "preact";
import { HelpLink, Related } from "./parts";

function Term({ name, children }: { name: string; children: ComponentChildren }) {
  return (
    <tr>
      <td>
        <strong>{name}</strong>
      </td>
      <td>{children}</td>
    </tr>
  );
}

export function Glossary() {
  return (
    <>
      <p>The words this app uses, in alphabetical order. Older screens and guides may still say the word in brackets.</p>
      <table>
        <thead>
          <tr>
            <th>Word</th>
            <th>Meaning</th>
          </tr>
        </thead>
        <tbody>
          <Term name="Actual AWS bill">
            What AWS really charged for the usage, read from the customer's Cost and Usage Report, discounts and credits
            included, tax excluded. One of the two cost bases. See <HelpLink to="aws-costs" />.
          </Term>
          <Term name="AWS account link">
            Permission for Nexus to work in a customer's AWS account, granted by a small CloudFormation stack the customer
            launches. The installer and the Cost and Usage Report both need one.
          </Term>
          <Term name="AWS cost (Expense)">
            What the customers' Bedrock usage cost AWS, on the chosen cost basis.
          </Term>
          <Term name="AWS list price">
            The price AWS publishes for each Bedrock model per million tokens, kept in Model prices. The default cost basis.
          </Term>
          <Term name="Billed">What a customer is invoiced: AWS cost with the rate plan applied, plus adjustments.</Term>
          <Term name="Cost basis (Basis)">
            Which AWS cost profit and markup are measured against: AWS list price or the actual AWS bill.
          </Term>
          <Term name="CUR">
            AWS's Cost and Usage Report: the detailed AWS bill, exported to a bucket in the customer's account.
          </Term>
          <Term name="Customer (Org)">
            A company whose developers use Nexus. Everything (users, installations, usage, billing) belongs to one customer.
          </Term>
          <Term name="Drift">
            How far the proxy's own token counts differ from what Bedrock reported for the same account, day and model. See{" "}
            <HelpLink to="reconciliation" />.
          </Term>
          <Term name="Gross profit (Fleet net margin)">
            Billed minus AWS cost. Across all customers it also subtracts the AWS cost of customers who are not billed but
            whose AWS bill AllCode pays. Platform running costs are not subtracted.
          </Term>
          <Term name="Installation">
            One proxy deployed in a customer's AWS account, with its own id and secret. See <HelpLink to="installations" />.
          </Term>
          <Term name="Installer session">
            One install or upgrade run by the installer: an AI agent proposes changes and a person approves each one.
          </Term>
          <Term name="Invoice">
            What a customer is charged for one month. A live preview while the month is open, fixed once it is closed.
          </Term>
          <Term name="Margin">
            Profit as a share of what was billed. A 30 % markup is a 23.1 % margin. See{" "}
            <HelpLink to="pricing-and-markup" section="markup-is-not-margin" />.
          </Term>
          <Term name="Markup">Profit as a share of AWS cost: what is added on top of cost.</Term>
          <Term name="Model prices (Price book)">The AWS list price of each Bedrock model, by the date it applies from.</Term>
          <Term name="Month (Billing period)">
            One customer's calendar month in UTC. It is <strong>open</strong> while usage can still change it and{" "}
            <strong>closed</strong> once its invoice is fixed.
          </Term>
          <Term name="Not billed">
            A customer or month that is metered but not invoiced (“proxy only”). Every customer starts not billed.
          </Term>
          <Term name="Rate plan">
            The commercial terms a customer is billed on: markup, per-model rates, volume tiers, a monthly minimum and a
            platform fee. See <HelpLink to="rate-plans" />.
          </Term>
          <Term name="Statement">
            The customer's own view of an invoice: the lines and the total, with no AWS cost or profit.
          </Term>
          <Term name="Status (State)">Where something stands, shown as a coloured chip: Active, Open, Closed, Draft…</Term>
          <Term name="Token types">
            Input, output, cache read, and cache write (5-minute and 1-hour). Each has its own price per million tokens.
          </Term>
          <Term name="Trial">
            Free usage up to a dollar credit or a number of tokens, between two dates. See <HelpLink to="trials-and-credits" />.
          </Term>
          <Term name="Usage adjustment">
            A credit or correction to a month's invoice, with a reason, approved before it counts. See{" "}
            <HelpLink to="usage-adjustments" />.
          </Term>
          <Term name="Usage event">One request through the proxy: who, which model, how many tokens of each type, when.</Term>
          <Term name="Who pays AWS">
            Whether AllCode or the customer pays AWS for the customer's Bedrock usage. It decides whether that cost is
            AllCode's; it does not change what the customer is billed.
          </Term>
        </tbody>
      </table>

      <Related to={["what-nexus-does", "pricing-and-markup", "aws-costs"]} />
    </>
  );
}
